import React, {useCallback} from 'react';

import {PanelTypesEnum} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useAppDispatch} from '../../helpers/appReduxHelpers';
import {ApplicationEntryPointTypesEnum} from '../../models/api/applicationEntryPointModel';
import {removePanelAction} from '../../store/panels/panelsActions';
import {PanelState} from '../../store/panels/panelsState';
import {PopupAccountDetails} from './popupAccountDetails';
import {PopupApplications} from './popupApplications';
import {PopupCalendarViewer} from './popupCalendarViewer';
import {PopupComposerContainer} from './popupComposerContainer';
import {PopupContactDetails} from './popupContactDetails';
import {PopupCrmPreview} from './popupCrmPreview';
import {PopupEmlContainer} from './popupEmlContainer';
import {PopupKnowledgeBasePlugin} from './popupKnowledgeBasePlugin';
import {PopupMessageSource} from './popupMessageSource';
import {PopupMessageViewerContainer} from './popupMessageViewerContainer';
import {PopupRuleEditor} from './popupRuleEditor';
import {PopupViewerContainer} from './popupViewerContainer';

/*
 * Props.
 */

interface PopupProps {
  panel: PanelState;
}

/*
 * Constants.
 */

const popupTimelineVariation = TimelineVariationsEnum.ASYNCHRONOUS;

/*
 * Component.
 */

export const Popup = (props: PopupProps) => {
  const {panel} = props;

  const dispatch = useAppDispatch();
  const onRequestClose = useCallback(() => {
    dispatch(removePanelAction(panel.id));
  }, [dispatch, panel.id]);

  // Otherwise, render by type.
  switch (panel.type) {
    case PanelTypesEnum.COMPOSER:
      return (
        <PopupComposerContainer
          panelId={panel.id}
          messageUid={panel.messageUid}
          shouldForcePopout={panel.shouldForcePopout}
          onRequestClose={onRequestClose}
          timelineVariation={popupTimelineVariation}
        />
      );

    case PanelTypesEnum.VIEWER:
      return <PopupViewerContainer panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.MESSAGE:
      return (
        <PopupMessageViewerContainer
          panelId={panel.id}
          {...panel}
          timelineVariation={popupTimelineVariation}
          onRequestClose={onRequestClose}
        />
      );

    case PanelTypesEnum.CALENDARS:
      return <PopupCalendarViewer panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.EML:
      return (
        <PopupEmlContainer
          panelId={panel.id}
          {...panel}
          timelineVariation={popupTimelineVariation}
          onRequestClose={onRequestClose}
        />
      );

    case PanelTypesEnum.CONTACT_DETAILS:
      return <PopupContactDetails panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.ACCOUNT_DETAILS:
      return <PopupAccountDetails panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.CRM_PREVIEW:
      return <PopupCrmPreview panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.APPLICATIONS:
      // Tighten the type to be inbox side panel because popup applications doesn't handle composer entry points.
      if (panel.frame.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL) {
        return (
          <PopupApplications
            panelId={panel.id}
            frame={panel.frame}
            popupWidth={panel.popupWidth}
            onRequestClose={onRequestClose}
          />
        );
      }
      return null;

    case PanelTypesEnum.MESSAGE_SOURCE:
      return <PopupMessageSource panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.RULE_EDITOR:
      return <PopupRuleEditor panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    case PanelTypesEnum.KNOWLEDGE_BASE_PLUGIN:
      return <PopupKnowledgeBasePlugin panelId={panel.id} {...panel} onRequestClose={onRequestClose} />;

    default:
      throw new Error('This panel type does not support popups.');
  }
};
